/**
 * Deterministic canonicalization of the ONE URL a merchant submits.
 *
 * The merchant gives us a single link. That link is doing two different jobs
 * at once, and conflating them is what makes a profile wrong:
 *
 *   * it identifies **a business** — whoever operates the site or account;
 *   * it identifies **an intent** — the specific product, service, course,
 *     post or page the merchant actually cared about when they pasted it.
 *
 * A deep product page answers the second question and is actively misleading
 * about the first: its title is a product name, its headings are marketing
 * copy, and its navigation is full of generic calls to action. So this module
 * derives both addresses from one submission and keeps them apart:
 *
 *   * `businessRootUrl` — the origin (or the social profile / link-hub
 *     creator page), used for identity, category, positioning and market;
 *   * `cleanedIntentUrl` — the submitted page with tracking removed, used
 *     for the selected offer, price and immediate intent.
 *
 * Everything here is pure and dependency-free: no network, no clock, no
 * randomness, no model. The same input always produces the same result, and
 * the exact submitted string is preserved verbatim for audit even when
 * normalization changes it.
 *
 * Two deliberate refusals to guess:
 *
 *   * Unknown query parameters are KEPT. Only parameters on the explicit
 *     tracking list are removed, because a parameter nobody recognises is far
 *     more likely to select a variant, a size or a page than to be a tracker,
 *     and silently dropping it would change which page we acquire.
 *   * A social handle is never invented. When the submitted URL carries no
 *     handle, the business root stays unresolved and is reported as such, so
 *     a later layer can resolve it from acquired metadata instead of this
 *     module fabricating an account that may not exist.
 */
/**
 * The version stamped onto every canonicalization result and persisted with
 * the source. Behaviour changes here must change this identifier so stored
 * rows remain auditable against the rules that produced them.
 */
export const SOURCE_CANONICALIZATION_VERSION = 'source-canonicalization.v1';
/** What kind of page the submitted URL points at. */
export const SOURCE_PAGE_KINDS = [
    'HOMEPAGE',
    'PRODUCT_PAGE',
    'SERVICE_PAGE',
    'COURSE_PAGE',
    'CATEGORY_PAGE',
    'MARKETPLACE_PRODUCT',
    'SOCIAL_PROFILE',
    'SOCIAL_POST',
    'LINK_HUB',
    'EDITORIAL_PAGE',
    'UNKNOWN',
];
/**
 * Why a business root has the value it does. `UNRESOLVED_SOCIAL` is the
 * honest state for a social post whose handle is not in the URL: the root
 * falls back to the submitted page and a later layer may resolve the real
 * profile from acquired metadata.
 */
export const BUSINESS_ROOT_BASES = [
    'ORIGIN',
    'SOCIAL_PROFILE',
    'LINK_HUB_CREATOR',
    'UNRESOLVED_SOCIAL',
];
/** The role an acquisition target plays; see `evidence-authority`. */
export const SOURCE_AUTHORITY_ROLES = ['BUSINESS_ROOT', 'SUBMITTED_INTENT'];
export const SOURCE_CANONICALIZATION_REFUSALS = [
    'EMPTY_INPUT',
    'INVALID_URL',
    'UNSUPPORTED_SCHEME',
    'EMBEDDED_CREDENTIALS',
    'INVALID_HOST',
];
/**
 * Query parameters removed case-insensitively because they carry campaign
 * attribution only and never select content. Anything not listed here (and
 * not matching a prefix below) is preserved.
 */
export const TRACKING_PARAMETER_NAMES = [
    'gclid',
    'gclsrc',
    'dclid',
    'gbraid',
    'wbraid',
    'gad_source',
    'gad_campaignid',
    'fbclid',
    'msclkid',
    'ttclid',
    'twclid',
    'yclid',
    'igshid',
    'igsh',
    'epik',
    'mkt_tok',
    'mc_cid',
    'mc_eid',
    's_kwcid',
    'li_fat_id',
    'vero_id',
    'vero_conv',
    '_hsenc',
    '_hsmi',
    'oly_anon_id',
    'oly_enc_id',
    'ref_src',
    'ref_url',
];
/**
 * Prefixes removed case-insensitively. Deliberately narrow: `utm_` is the
 * only family broad enough to strip wholesale. Mailchimp's `mc_` family is
 * NOT a prefix rule, because `mc_` also appears on functional parameters;
 * only its two known marketing parameters are listed above.
 */
export const TRACKING_PARAMETER_PREFIXES = ['utm_'];
const SOCIAL_HOSTS = {
    'instagram.com': 'INSTAGRAM',
    'tiktok.com': 'TIKTOK',
};
/** Link-in-bio resolvers: the page is a directory of a creator's real links. */
const LINK_HUB_HOSTS = [
    'linktr.ee',
    'linktree.com',
    'beacons.ai',
    'bio.link',
    'taplink.cc',
    'solo.to',
    'lnk.bio',
    'campsite.bio',
    'msha.ke',
    'shorby.com',
    'linkin.bio',
];
/** Instagram path prefixes that are posts rather than accounts. */
const INSTAGRAM_POST_SEGMENTS = ['p', 'reel', 'reels', 'tv'];
/** Instagram paths that are platform surfaces, never a business account. */
const INSTAGRAM_RESERVED_SEGMENTS = [
    'explore',
    'accounts',
    'direct',
    'stories',
    'about',
    'developer',
    'legal',
    'privacy',
    'terms',
    'challenge',
];
const PRODUCT_SEGMENTS = [
    'product',
    'products',
    'item',
    'items',
    'dp',
    'sku',
    'listing',
    'buy',
];
const COURSE_SEGMENTS = [
    'course',
    'courses',
    'class',
    'classes',
    'training',
    'bootcamp',
    'curriculum',
    'lesson',
    'lessons',
];
const SERVICE_SEGMENTS = [
    'service',
    'services',
    'solutions',
    'treatment',
    'treatments',
    'package',
    'packages',
    'booking',
    'appointments',
];
const CATEGORY_SEGMENTS = [
    'category',
    'categories',
    'collection',
    'collections',
    'catalog',
    'catalogue',
    'shop',
    'store',
    'browse',
    'search',
];
const EDITORIAL_SEGMENTS = [
    'blog',
    'article',
    'articles',
    'news',
    'press',
    'stories',
    'guide',
    'guides',
    'magazine',
    'journal',
];
/**
 * A product-detail slug: several words followed by a numeric identifier, the
 * shape almost every storefront uses for a single sellable item
 * (`…-cotton-blend-for-women-219205806_7762`). Generic on purpose — no host,
 * brand or market is encoded anywhere in this module.
 */
const PRODUCT_SLUG_ID = /[-_](\d{4,})(?:[-_][A-Za-z0-9]{1,12})?$/u;
function refuse(refusal, message) {
    return { ok: false, refusal, message };
}
function isTrackingParameter(name) {
    const lowered = name.toLowerCase();
    return (TRACKING_PARAMETER_NAMES.includes(lowered) ||
        TRACKING_PARAMETER_PREFIXES.some((prefix) => lowered.startsWith(prefix)));
}
/** Registrable-ish host comparison: `x.com` matches `a.b.x.com`. */
function hostMatches(host, domain) {
    return host === domain || host.endsWith(`.${domain}`);
}
function bareHost(host) {
    return host.replace(/^www\./u, '');
}
function segmentsOf(pathname) {
    return pathname.split('/').filter((segment) => segment.length > 0);
}
/**
 * Serialize without the fragment and without a default port, preserving the
 * path exactly as given (an added or removed trailing slash can change which
 * page a server returns, so neither is invented here).
 */
function serialize(url, search) {
    const port = (url.protocol === 'https:' && url.port === '443') ||
        (url.protocol === 'http:' && url.port === '80')
        ? ''
        : url.port;
    const authority = port === '' ? url.hostname : `${url.hostname}:${port}`;
    const path = url.pathname === '' ? '/' : url.pathname;
    return `${url.protocol}//${authority}${path}${search}`;
}
function originRoot(url) {
    const port = (url.protocol === 'https:' && url.port === '443') ||
        (url.protocol === 'http:' && url.port === '80')
        ? ''
        : url.port;
    const authority = port === '' ? url.hostname : `${url.hostname}:${port}`;
    return `${url.protocol}//${authority}/`;
}
function instagramShape(segments) {
    const first = segments[0]?.toLowerCase() ?? '';
    const second = segments[1]?.toLowerCase() ?? '';
    if (first === '' || INSTAGRAM_RESERVED_SEGMENTS.includes(first)) {
        return {
            sourceKind: 'INSTAGRAM',
            platform: 'instagram',
            handle: null,
            pageKind: first === '' ? 'SOCIAL_PROFILE' : 'UNKNOWN',
        };
    }
    // `/p/{code}` and friends carry no handle; the account stays unresolved.
    if (INSTAGRAM_POST_SEGMENTS.includes(first)) {
        return {
            sourceKind: 'INSTAGRAM',
            platform: 'instagram',
            handle: null,
            pageKind: 'SOCIAL_POST',
        };
    }
    const handle = segments[0] ?? null;
    if (INSTAGRAM_POST_SEGMENTS.includes(second)) {
        return {
            sourceKind: 'INSTAGRAM',
            platform: 'instagram',
            handle,
            pageKind: 'SOCIAL_POST',
        };
    }
    return {
        sourceKind: 'INSTAGRAM',
        platform: 'instagram',
        handle,
        pageKind: 'SOCIAL_PROFILE',
    };
}
function tiktokShape(segments) {
    const first = segments[0] ?? '';
    if (first.startsWith('@')) {
        const handle = first.slice(1);
        const isVideo = (segments[1]?.toLowerCase() ?? '') === 'video';
        return {
            sourceKind: 'TIKTOK',
            platform: 'tiktok',
            handle: handle.length > 0 ? handle : null,
            pageKind: isVideo ? 'SOCIAL_POST' : 'SOCIAL_PROFILE',
        };
    }
    // Short links (`/t/{id}`) and platform surfaces carry no handle.
    return {
        sourceKind: 'TIKTOK',
        platform: 'tiktok',
        handle: null,
        pageKind: first === '' ? 'SOCIAL_PROFILE' : 'SOCIAL_POST',
    };
}
/**
 * Provisional page kind from the URL alone. Deliberately conservative:
 * explicit route markers win, a product-identifier slug is the fallback, and
 * anything else stays UNKNOWN rather than being guessed from prose. Page
 * metadata refines this later through {@link refineSourcePageKind}.
 */
export function classifyPathPageKind(pathname) {
    const segments = segmentsOf(pathname);
    if (segments.length === 0)
        return 'HOMEPAGE';
    const lowered = segments.map((segment) => segment.toLowerCase());
    if (lowered.some((segment) => PRODUCT_SEGMENTS.includes(segment))) {
        return 'PRODUCT_PAGE';
    }
    if (lowered.some((segment) => COURSE_SEGMENTS.includes(segment))) {
        return 'COURSE_PAGE';
    }
    if (lowered.some((segment) => SERVICE_SEGMENTS.includes(segment))) {
        return 'SERVICE_PAGE';
    }
    if (lowered.some((segment) => EDITORIAL_SEGMENTS.includes(segment))) {
        return 'EDITORIAL_PAGE';
    }
    if (lowered.some((segment) => CATEGORY_SEGMENTS.includes(segment))) {
        return 'CATEGORY_PAGE';
    }
    const last = lowered[lowered.length - 1] ?? '';
    const withoutExtension = last.replace(/\.(?:html?|php|aspx?|jsp)$/u, '');
    const words = withoutExtension.split(/[-_]/u).filter((part) => part.length > 0);
    if (PRODUCT_SLUG_ID.test(withoutExtension) && words.length >= 3) {
        return 'PRODUCT_PAGE';
    }
    return 'UNKNOWN';
}
/**
 * Canonicalize one submitted URL.
 *
 * Accepts scheme-less input (`ounass.com`, `www.example.com/p/1`) and
 * defaults it to HTTPS. Refuses anything that is not HTTP(S) and anything
 * carrying embedded credentials — those are rejected outright rather than
 * stripped, because a URL with a password in it is not a public business
 * source and quietly repairing it would hide that.
 */
export function canonicalizeSubmittedSource(submitted) {
    const originalUrl = submitted;
    const trimmed = submitted.trim();
    if (trimmed.length === 0) {
        return refuse('EMPTY_INPUT', 'No URL was submitted.');
    }
    if (/\s/u.test(trimmed)) {
        return refuse('INVALID_URL', 'A URL may not contain whitespace.');
    }
    const declaredScheme = trimmed.match(/^([A-Za-z][A-Za-z0-9+.-]*):/u)?.[1];
    if (declaredScheme !== undefined) {
        const scheme = declaredScheme.toLowerCase();
        if (scheme !== 'http' && scheme !== 'https') {
            return refuse('UNSUPPORTED_SCHEME', `Only http and https sources are supported; received ${scheme}.`);
        }
    }
    let url;
    try {
        url = new URL(declaredScheme === undefined ? `https://${trimmed}` : trimmed);
    }
    catch {
        return refuse('INVALID_URL', 'The submitted value is not a valid URL.');
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return refuse('UNSUPPORTED_SCHEME', 'Only http and https sources are supported.');
    }
    if (url.username !== '' || url.password !== '') {
        return refuse('EMBEDDED_CREDENTIALS', 'A source URL must not carry an embedded username or password.');
    }
    // `URL` already lowercases the scheme and host; a trailing dot is a valid
    // but non-canonical spelling of the same host and is removed.
    url.hostname = url.hostname.replace(/\.$/u, '');
    const host = url.hostname.toLowerCase();
    if (!host.includes('.') || host.startsWith('.') || host.startsWith('-')) {
        return refuse('INVALID_HOST', 'The submitted host is not a public domain name.');
    }
    url.hash = '';
    const removedTrackingParameters = [];
    const preservedFunctionalParameters = [];
    const kept = new URLSearchParams();
    for (const [name, value] of url.searchParams) {
        if (isTrackingParameter(name)) {
            removedTrackingParameters.push(name);
            continue;
        }
        preservedFunctionalParameters.push(name);
        kept.append(name, value);
    }
    const keptSearch = kept.toString();
    const submittedSearch = url.search;
    const cleanedSearch = keptSearch === '' ? '' : `?${keptSearch}`;
    const normalizedSubmittedUrl = serialize(url, submittedSearch);
    const cleanedIntentUrl = serialize(url, cleanedSearch);
    const segments = segmentsOf(url.pathname);
    const compareHost = bareHost(host);
    let sourceKind = 'WEBSITE';
    let platform = null;
    let handle = null;
    let pageKind;
    let businessRootUrl;
    let businessRootBasis;
    const socialDomain = Object.keys(SOCIAL_HOSTS).find((domain) => hostMatches(compareHost, domain));
    const linkHubDomain = LINK_HUB_HOSTS.find((domain) => hostMatches(compareHost, domain));
    if (socialDomain !== undefined) {
        const shape = SOCIAL_HOSTS[socialDomain] === 'INSTAGRAM'
            ? instagramShape(segments)
            : tiktokShape(segments);
        sourceKind = shape.sourceKind;
        platform = shape.platform;
        handle = shape.handle;
        pageKind = shape.pageKind;
        if (shape.handle === null) {
            // The platform is never the business, and a handle is never invented:
            // the root stays the submitted page and is flagged unresolved.
            businessRootUrl = cleanedIntentUrl;
            businessRootBasis = 'UNRESOLVED_SOCIAL';
        }
        else {
            const profilePath = shape.sourceKind === 'TIKTOK' ? `@${shape.handle}` : `${shape.handle}/`;
            businessRootUrl = `${originRoot(url)}${profilePath}`;
            businessRootBasis = 'SOCIAL_PROFILE';
        }
    }
    else if (linkHubDomain !== undefined) {
        // A link hub resolves to somebody else's real sources; the hub itself is
        // never the business, so the creator page is the root we acquire.
        sourceKind = 'WEBSITE';
        platform = linkHubDomain;
        handle = segments[0] ?? null;
        pageKind = 'LINK_HUB';
        businessRootUrl = handle === null ? originRoot(url) : `${originRoot(url)}${handle}`;
        businessRootBasis = handle === null ? 'ORIGIN' : 'LINK_HUB_CREATOR';
    }
    else {
        sourceKind = 'WEBSITE';
        pageKind = classifyPathPageKind(url.pathname);
        businessRootUrl = originRoot(url);
        businessRootBasis = 'ORIGIN';
    }
    return {
        ok: true,
        source: {
            canonicalizationVersion: SOURCE_CANONICALIZATION_VERSION,
            originalUrl,
            normalizedSubmittedUrl,
            cleanedIntentUrl,
            businessRootUrl,
            businessRootBasis,
            sourceKind,
            pageKind,
            platform,
            handle,
            removedTrackingParameters,
            preservedFunctionalParameters,
            singleAcquisition: businessRootUrl === cleanedIntentUrl,
        },
    };
}
function matchesType(types, wanted) {
    return types.some((type) => type.toLowerCase() === wanted.toLowerCase());
}
/**
 * Refine the provisional page kind using page evidence.
 *
 * Structured data and Open Graph beat route text, because a path is a naming
 * convention while `@type: Product` with a price is an assertion by the site
 * itself. Social and link-hub classifications are host-level facts and are
 * never overridden here.
 */
export function refineSourcePageKind(provisional, signals) {
    if (provisional === 'SOCIAL_PROFILE' ||
        provisional === 'SOCIAL_POST' ||
        provisional === 'LINK_HUB') {
        return provisional;
    }
    const types = signals.structuredDataTypes ?? [];
    const openGraphType = (signals.openGraphType ?? '').toLowerCase();
    if (matchesType(types, 'Course'))
        return 'COURSE_PAGE';
    if (matchesType(types, 'Service'))
        return 'SERVICE_PAGE';
    if (matchesType(types, 'Product') ||
        matchesType(types, 'ProductGroup') ||
        matchesType(types, 'Offer') ||
        openGraphType === 'product' ||
        openGraphType === 'product.item') {
        return signals.sellerDistinctFromOperator === true
            ? 'MARKETPLACE_PRODUCT'
            : 'PRODUCT_PAGE';
    }
    if (matchesType(types, 'CollectionPage') || matchesType(types, 'ItemList')) {
        return 'CATEGORY_PAGE';
    }
    if (matchesType(types, 'Article') ||
        matchesType(types, 'NewsArticle') ||
        matchesType(types, 'BlogPosting') ||
        openGraphType === 'article') {
        return 'EDITORIAL_PAGE';
    }
    // A price with a currency is a strong commercial assertion on a page whose
    // route told us nothing.
    if (provisional === 'UNKNOWN' && signals.hasPriceAndCurrency === true) {
        return 'PRODUCT_PAGE';
    }
    return provisional;
}
/**
 * Whether this URL is its own business root — a homepage, a social profile,
 * a link-hub creator page.
 *
 * The test that matters for evidence authority: a page that is not the root
 * is a deep page, and a deep page's title describes one product, service or
 * post rather than the business behind it.
 */
export function isBusinessRootUrl(url) {
    const result = canonicalizeSubmittedSource(url);
    return result.ok && result.source.singleAcquisition;
}
/**
 * The acquisition plan for one submission: the business root, plus the
 * submitted intent when it is a different address. When the merchant
 * submitted the homepage itself, exactly one target is returned so the page
 * is not fetched twice.
 */
export function sourceAcquisitionTargets(source) {
    const root = {
        url: source.businessRootUrl,
        authorityRole: 'BUSINESS_ROOT',
        pageKind: source.businessRootBasis === 'ORIGIN'
            ? 'HOMEPAGE'
            : source.businessRootBasis === 'SOCIAL_PROFILE'
                ? 'SOCIAL_PROFILE'
                : source.pageKind,
    };
    if (source.singleAcquisition)
        return [root];
    return [
        root,
        {
            url: source.cleanedIntentUrl,
            authorityRole: 'SUBMITTED_INTENT',
            pageKind: source.pageKind,
        },
    ];
}
//# sourceMappingURL=source-canonicalization.js.map